import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";

import { api } from "../lib/projects-api.ts";

const IS_MAC = typeof navigator !== "undefined"
  && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || "");

function isEditableTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return true;
  return target.isContentEditable || Boolean(target.closest('[contenteditable="true"]'));
}

function Heading({ children }) {
  return (
    <Typography variant="caption" color="text.secondary"
      sx={{ px: 2, pt: 1.5, pb: 0.5, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}>
      {children}
    </Typography>
  );
}

/**
 * Cmd+K (or `/` outside an editable field) jumps straight to a project or a
 * task by title, "KEY-N", or a bare task number — the monolith's GlobalSearch,
 * ported. No full-text index backs it: this app's tables are DynamoDB, not
 * the monolith's Postgres, so the server does a substring scan over what the
 * caller can already read rather than reaching for the org's OpenSearch
 * domain, which belongs to case-management and is IAM-locked to it.
 */
export default function GlobalSearch({ onOpenProject, onOpenTask }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [data, setData] = useState({ results: [], projects: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (e.key === "/" && !isEditableTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) {
      setQ("");
      setDebounced("");
      setData({ results: [], projects: [] });
      return;
    }
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (debounced.length < 2) { setData({ results: [], projects: [] }); return; }
    let live = true;
    setLoading(true);
    api.search(debounced)
      .then((r) => { if (live) setData(r); })
      .catch(() => { if (live) setData({ results: [], projects: [] }); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [debounced]);

  const selectProject = (p) => { setOpen(false); onOpenProject(p); };
  const selectTask = (r) => { setOpen(false); onOpenTask(r); };

  const nothing = debounced.length >= 2 && !loading
    && data.results.length === 0 && data.projects.length === 0;

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)} fullWidth
        sx={{ justifyContent: "flex-start", color: "text.secondary", fontWeight: 400, px: 1.5 }}>
        <Box component="span" sx={{ fontSize: 15, mr: 1, opacity: 0.7 }}>&#8981;</Box>
        <Box sx={{ flex: 1, textAlign: "left" }}>Search…</Box>
        <Chip size="small" variant="outlined" label={IS_MAC ? "⌘K" : "Ctrl+K"}
          sx={{ fontFamily: "monospace", height: 20, fontSize: 10 }} />
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm"
        slotProps={{ paper: { sx: { position: "fixed", top: 96, m: 2 } } }}>
        <DialogContent sx={{ p: 0 }}>
          <TextField
            inputRef={inputRef} fullWidth value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tasks and projects…"
            variant="standard"
            sx={{ px: 2, pt: 2, pb: 1 }}
            slotProps={{
              input: { disableUnderline: true },
              htmlInput: { "aria-label": "Search tasks and projects" },
            }} />
          <List sx={{ maxHeight: 420, overflowY: "auto", pb: 1 }}>
            {debounced.length < 2 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                Type at least 2 characters.
              </Typography>
            ) : nothing ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                No matches.
              </Typography>
            ) : (
              <>
                {data.projects.length ? (
                  <>
                    <Heading>Projects</Heading>
                    {data.projects.map((p) => (
                      <ListItemButton key={p.id} onClick={() => selectProject(p)}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "3px", mr: 1.5, flexShrink: 0,
                                   bgcolor: p.color || "primary.main" }} />
                        <ListItemText primary={p.name} />
                        <Chip size="small" variant="outlined" label={p.key}
                          sx={{ fontFamily: "monospace" }} />
                      </ListItemButton>
                    ))}
                  </>
                ) : null}
                {data.results.length ? (
                  <>
                    <Heading>Tasks</Heading>
                    {data.results.map((r) => (
                      <ListItemButton key={r.id} onClick={() => selectTask(r)}>
                        <ListItemText primary={r.title}
                          secondary={`${r.projectKey}-${r.number} · ${r.projectName}`} />
                        {r.status === "done" ? (
                          <Chip size="small" color="success" variant="outlined" label="done" />
                        ) : null}
                      </ListItemButton>
                    ))}
                  </>
                ) : null}
              </>
            )}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
}
